/**
 * Server-Sent Events of GET /api/v1/events (specs/c2-kontratlar.md 3). Every event is
 * an unnamed message ("id: <bootId>.<seq>" and "data: <json>", no "event:" field, the
 * panel uses onmessage). The heartbeat is a data message too, sent only while the API
 * listener is healthy. Events carry whole objects, never patches.
 */
import { z } from 'zod';
import { instantSchema } from './ids.js';
import { agentSchema, doneItemSchema, handoffSchema, liveFeedItemSchema, panelSystemSchema, proposalSchema, runSchema, twinRequestSchema, } from './panel.js';
export const HEARTBEAT_INTERVAL_MS = 15_000;
/** The panel treats more than 30 s without a heartbeat as stale and disables every approve control. */
export const STALE_AFTER_MS = 30_000;
/** First line of every stream: "retry: 3000". */
export const SSE_RETRY_MS = 3000;
/** Replay buffer: the last 2000 events or 15 minutes, whichever is smaller. */
export const SSE_REPLAY_MAX_EVENTS = 2000;
export const SSE_REPLAY_MAX_AGE_MS = 15 * 60 * 1000;
/** Changes of the same entity within 150 ms are sent once. */
export const SSE_DEBOUNCE_MS = 150;
/** The panel rebuilds a closed EventSource with this backoff (ms). */
export const SSE_RECONNECT_BACKOFF_MS = Object.freeze([1000, 2000, 4000, 8000, 16_000, 30_000]);
const nonEmpty = z.string().min(1);
export const liveEventSchema = z.discriminatedUnion('type', [
    // Present before C2.
    z.object({ type: z.literal('heartbeat'), at: instantSchema }),
    z.object({ type: z.literal('agent'), agent: agentSchema }),
    z.object({ type: z.literal('run'), run: runSchema }),
    z.object({ type: z.literal('feed'), item: liveFeedItemSchema }),
    z.object({ type: z.literal('handoff'), handoff: handoffSchema }),
    z.object({ type: z.literal('proposal.added'), proposal: proposalSchema }),
    z.object({
        type: z.literal('proposal.removed'),
        proposalId: nonEmpty,
        /** C2, optional: older panels ignore it. */
        reason: z.enum(['approved', 'rejected', 'expired', 'withdrawn', 'undone']).optional(),
    }),
    z.object({ type: z.literal('request.added'), request: twinRequestSchema }),
    z.object({ type: z.literal('executor.applied'), ticketId: nonEmpty, proposalId: nonEmpty, at: instantSchema }),
    z.object({ type: z.literal('executor.failed'), ticketId: nonEmpty, proposalId: nonEmpty, reason: z.string() }),
    // New in C2.
    /** The Denetçi verdict arrived. */
    z.object({ type: z.literal('proposal.updated'), proposal: proposalSchema }),
    z.object({ type: z.literal('request.updated'), request: twinRequestSchema }),
    z.object({ type: z.literal('request.removed'), requestId: nonEmpty }),
    /** A Yapılanlar row was added or changed. */
    z.object({ type: z.literal('done'), item: doneItemSchema }),
    /** Kill switch or dry run changed. */
    z.object({ type: z.literal('system'), system: panelSystemSchema }),
    z.object({
        type: z.literal('executor.dry_run'),
        ticketId: nonEmpty,
        proposalId: nonEmpty,
        at: instantSchema,
        summary: z.string(),
    }),
    z.object({ type: z.literal('snapshot.invalidate'), reason: z.enum(['gap', 'listener_recovered', 'server_restart']) }),
]);
export const LIVE_EVENT_TYPES = Object.freeze(liveEventSchema.options.map((option) => option.shape.type.value));
/**
 * Parses the data of one SSE message. Unknown types and malformed events return null
 * and are dropped rather than patching the panel state with the wrong shape.
 */
export function parseLiveEvent(data) {
    let value;
    try {
        value = JSON.parse(data);
    }
    catch {
        return null;
    }
    const parsed = liveEventSchema.safeParse(value);
    return parsed.success ? parsed.data : null;
}
/** One SSE frame as the API writes it: id line, data line, blank line. */
export function formatSseFrame(eventId, event) {
    return `id: ${eventId}\ndata: ${JSON.stringify(event)}\n\n`;
}
//# sourceMappingURL=sse.js.map